// The Pulse engine: pure, deterministic, no DOM. It writes an endless ECG "tape" from mood programs and cuts it into
// strips: one SVG path plus the keyframes that scroll it (translateX) and carry the beam head (translateY, scale,
// opacity). The component plays those keyframes with the Web Animations API, so every frame runs on the compositor.
//
// Units: x in px along the tape, time in seconds, y in amplitude units (1 = a full R wave, positive is up).

export type PulseMood = 'steady' | 'racing' | 'spike' | 'jolt' | 'flat' | 'crown';
export const PULSE_MOODS: readonly PulseMood[] = ['steady', 'racing', 'spike', 'jolt', 'flat', 'crown'];

/** A point on a piece. u is the fraction along the piece (0–1); s and o are the beam head's scale and opacity there. */
export interface Knot { u: number; y: number; s?: number; o?: number }
export type Extra = { kind: 'dot'; u: number; y: number; r: number } | { kind: 'line'; u0: number; u1: number; y: number };
export interface Piece {
  name: string;
  len: number;        // px of tape
  dur: number;        // seconds for the head to cross it
  knots: Knot[];      // sorted by u, first at u = 0 and last at u = 1, both at y = 0 unless the piece is a bridge
  extras?: Extra[];   // drawn with the trace (the crown's jewels and band)
  shakeAt?: number;   // u at which the whole Pulse jolts (defib)
  sparksAt?: number;  // u at which sparks fly from the head (celebration)
}
export interface Program { intro: Piece[]; loop: Piece[] }
export interface Scale {
  /** px of tape per beat at 72 bpm */
  spacing: number;
}

const TAU = Math.PI * 2;
const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
/** Integer hash to [0, 1), deterministic across platforms. */
export function hash01(n: number): number {
  let x = Math.imul((n | 0) ^ 0x5bd1e995, 0x9e3779b1);
  x = Math.imul(x ^ (x >>> 15), 0x2c1b3c6d);
  x = Math.imul(x ^ (x >>> 12), 0x297a2d39);
  x ^= x >>> 15;
  return (x >>> 0) / 4294967296;
}
const jitter = (seed: number, i: number, amount: number) => 1 + (hash01(seed * 7919 + i) * 2 - 1) * amount;

/** Tape speed in px per second (the tape always moves; beats get closer when the heart races). */
export const speedFor = (sc: Scale) => sc.spacing * 1.2; // 72 bpm = 1.2 beats a second

/** How many px of tape make one beat at 72 bpm for a Pulse of this size: about 1.4 to 5 beats in view. */
export function scaleFor(width: number, height: number): Scale {
  const beatsInView = clamp(width / Math.max(1, height * 3), 1.4, 5);
  return { spacing: Math.max(24, width / beatsInView) };
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** Samples a bump (two half-Gaussians) between c − 3σl and c + 3σr, exactly 0 at both ends. */
function bump(c: number, amp: number, sl: number, sr: number, n: number): Array<[number, number]> {
  const edge = Math.exp(-4.5);
  const out: Array<[number, number]> = [];
  for (let i = 0; i <= n; i++) {
    const k = i / n;
    const t = k < 0.5 ? c - 3 * sl * (1 - 2 * k) : c + 3 * sr * (2 * k - 1);
    const d = t < c ? (t - c) / sl : (t - c) / sr;
    const g = (Math.exp(-0.5 * d * d) - edge) / (1 - edge);
    out.push([t, amp * Math.max(0, g)]);
  }
  return out;
}

function finish(name: string, pts: Array<[number, number] | Knot>, dur: number, len: number, extra?: Partial<Piece>, head: { o?: number } = {}): Piece {
  const knots: Knot[] = pts.map((p) => (Array.isArray(p) ? { u: p[0] / dur, y: p[1] } : { ...p }));
  knots.sort((a, b) => a.u - b.u);
  // Clamp into [0, 1] and make sure the piece begins and ends on its edges.
  for (const k of knots) k.u = clamp(k.u, 0, 1);
  if (knots[0].u > 0) knots.unshift({ u: 0, y: 0 });
  if (knots[knots.length - 1].u < 1) knots.push({ u: 1, y: 0 });
  if (head.o !== undefined) for (const k of knots) k.o ??= head.o;
  return { name, len, dur, knots, ...extra };
}

/** One sinus beat: P, QRS, T. `amp` scales the whole complex. */
export function sinus(sc: Scale, bpm: number, opts: { amp?: number; speed?: number; seed?: number; i?: number; accent?: number; tremor?: number; dim?: number } = {}): Piece {
  const amp = opts.amp ?? 1;
  const rr = 60 / bpm;
  const v = speedFor(sc) * (opts.speed ?? 1);
  const k = Math.min(1, rr / 0.62); // compress the complex when the beats come close together
  const T = (t: number) => t * k;
  const head = opts.accent ?? 1.32; // the head pops on the R wave
  const o = opts.dim;
  const pts: Array<[number, number] | Knot> = [];
  for (const [t, y] of bump(T(0.1), 0.11 * amp, 0.026 * k, 0.026 * k, 8)) pts.push([t, y]);
  pts.push({ u: T(0.192) / rr, y: 0 }, { u: T(0.21) / rr, y: -0.09 * amp, s: 1.08 }, { u: T(0.236) / rr, y: 1.0 * amp, s: head },
    { u: T(0.262) / rr, y: -0.28 * amp, s: 1.08 }, { u: T(0.284) / rr, y: 0 });
  const tc = 0.284 + 0.16 * Math.sqrt(rr / 0.833); // QT shortens as the rate rises
  for (const [t, y] of bump(T(tc), 0.24 * amp, 0.056 * k, 0.042 * k, 10)) pts.push([t, y]);
  if (opts.tremor) {
    // A faint high-frequency tremor along the TP segment: urgency, not noise.
    const start = T(tc) + 3 * 0.042 * k; const n = Math.max(0, Math.floor((rr - start) / 0.03));
    for (let j = 1; j < n; j++) pts.push([start + j * 0.03, (hash01((opts.seed ?? 1) * 131 + (opts.i ?? 0) * 17 + j) * 2 - 1) * opts.tremor]);
  }
  return finish('sinus', pts, rr, v * rr, undefined, { o });
}

/** A flat stretch of tape. With `drift`, a slow periodic wander that still starts and ends on the baseline. */
export function flat(sc: Scale, seconds: number, opts: { drift?: number; speed?: number; dim?: number; scale?: number } = {}): Piece {
  const v = speedFor(sc) * (opts.speed ?? 1);
  const pts: Knot[] = [];
  const n = opts.drift ? 24 : 1;
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const y = opts.drift ? opts.drift * (Math.sin(TAU * u) + 0.45 * Math.sin(3 * TAU * u)) : 0;
    pts.push({ u, y, s: opts.scale, o: opts.dim });
  }
  return finish('flat', pts, seconds, v * seconds);
}

/** The celebration: a towering spike that rings out like a bell, sparks flying from the head. */
export function burst(sc: Scale): Piece {
  const dur = 0.46;
  const v = speedFor(sc) * 1.05;
  const pts: Array<[number, number] | Knot> = [];
  const seq: Array<[number, number, number?]> = [
    [0.035, -0.14], [0.085, 1.48, 2.1], [0.125, -0.56, 1.4], [0.158, 0.9, 1.7], [0.19, -0.36, 1.2], [0.22, 0.56, 1.45],
    [0.25, -0.18, 1.1], [0.28, 0.26, 1.15], [0.31, 0],
  ];
  pts.push({ u: 0, y: 0 });
  for (const [t, y, s] of seq) pts.push({ u: t / dur, y, s });
  return finish('burst', pts, dur, v * dur, { sparksAt: 0.085 / dur });
}

/** The falter before the jolt: the trace sags and quivers. */
export function wobble(sc: Scale, seed: number): Piece {
  const dur = 0.56;
  const v = speedFor(sc);
  const pts: Knot[] = [{ u: 0, y: 0 }];
  const n = 40;
  for (let i = 1; i < n; i++) {
    const u = i / n; const t = u * dur;
    const sag = -0.2 * Math.sin(Math.PI * u) ** 1.4;
    const env = Math.sin(Math.PI * u) ** 0.6;
    const q = (0.06 + 0.05 * hash01(seed * 31 + i)) * env * Math.sin(TAU * 13.5 * t + hash01(seed + i) * 1.2);
    pts.push({ u, y: sag + q, s: 0.92 });
  }
  pts.push({ u: 1, y: 0 });
  return finish('wobble', pts, dur, v * dur);
}

/** The defibrillator: a vertical shock artefact, then the line catches its breath. */
export function shock(sc: Scale): Piece {
  const dur = 0.2;
  const v = speedFor(sc);
  const seq: Array<[number, number, number?]> = [[0.004, 0], [0.014, 1.42, 1.9], [0.034, -0.86, 1.5], [0.058, 0.38, 1.2], [0.082, -0.13, 1.05], [0.104, 0]];
  const pts: Knot[] = seq.map(([t, y, s]) => ({ u: t / dur, y, s }));
  return finish('shock', pts, dur, v * dur, { shakeAt: 0.014 / dur });
}

/** The crown traced at the podium: three points, the middle tallest, with jewels on the tips and a band beneath. */
export function crown(sc: Scale, height = sc.spacing / 3): Piece {
  // A crown is about 1.6 times as wide as it is tall, whatever the tape speed.
  const len = Math.min(sc.spacing * 1.1, height * 1.45);
  const dur = 1.2; // the head slows down to draw it
  const tips: Array<[number, number]> = [[0.1, 1.18], [0.5, 1.58], [0.9, 1.18]];
  const knots: Knot[] = [
    { u: 0, y: 0 }, { u: 0.08, y: 0 }, { u: tips[0][0], y: tips[0][1], s: 1.75 }, { u: 0.3, y: 0.52, s: 1.1 },
    { u: tips[1][0], y: tips[1][1], s: 2 }, { u: 0.7, y: 0.52, s: 1.1 }, { u: tips[2][0], y: tips[2][1], s: 1.75 }, { u: 0.92, y: 0 }, { u: 1, y: 0 },
  ];
  const extras: Extra[] = [{ kind: 'line', u0: 0.08, u1: 0.92, y: 0 }, ...tips.map(([u, y]) => ({ kind: 'dot' as const, u, y: y + 0.16, r: 1.45 }))];
  return { name: 'crown', len, dur, knots, extras, sparksAt: 0.5 };
}

/** A smooth handover from wherever the head is to the baseline, so a mood change never jumps. */
export function bridge(sc: Scale, fromY: number): Piece {
  const dur = 0.07;
  const v = speedFor(sc);
  return { name: 'bridge', len: v * dur, dur, knots: [{ u: 0, y: fromY }, { u: 0.55, y: fromY * 0.2 }, { u: 1, y: 0 }] };
}

/** A single accented beat: "the Pulse ticks once" (lock-in). */
export const accentBeat = (sc: Scale) => sinus(sc, 96, { amp: 1.16, accent: 1.85 });

// ---------------------------------------------------------------------------
// Mood programs
// ---------------------------------------------------------------------------

/** What each mood plays: an intro once, then a loop forever. */
export function moodProgram(mood: PulseMood, sc: Scale, seed = 1, bpm?: number, height?: number): Program {
  const beats = (n: number, rate: number, o: Parameters<typeof sinus>[2] = {}, hrv = 0.035) =>
    Array.from({ length: n }, (_, i) => sinus(sc, rate * jitter(seed, i, hrv), { seed, i, ...o, amp: (o.amp ?? 1) * jitter(seed + 3, i, 0.025) }));
  switch (mood) {
    case 'steady': return { intro: [], loop: beats(8, bpm ?? 72) };
    case 'racing': return { intro: [], loop: beats(10, bpm ?? 162, { amp: 1.06, speed: 1.12, tremor: 0.022, accent: 1.45 }, 0.015) };
    case 'spike': return {
      intro: [flat(sc, 0.05), burst(sc), flat(sc, 0.08), ...beats(2, 108, { amp: 1.1, accent: 1.5 }), sinus(sc, 94, { amp: 1.05 })],
      loop: beats(8, bpm ?? 84),
    };
    case 'jolt': return {
      intro: [wobble(sc, seed), shock(sc), flat(sc, 0.24, { scale: 0.9 }), sinus(sc, 62, { amp: 0.8 }), sinus(sc, 68, { amp: 0.92 })],
      loop: beats(8, bpm ?? 72),
    };
    case 'flat': return {
      intro: [sinus(sc, 58, { amp: 0.5, accent: 1.1 }), sinus(sc, 46, { amp: 0.22, accent: 1, dim: 0.75 })],
      loop: [flat(sc, 2.6, { drift: 0.016, dim: 0.5, scale: 0.82 })],
    };
    case 'crown': return {
      intro: [flat(sc, 0.3)],
      loop: [crown(sc, height), flat(sc, 0.4), sinus(sc, 84), sinus(sc, 80), flat(sc, 0.35)],
    };
  }
}

// ---------------------------------------------------------------------------
// The tape: pieces laid end to end, generated on demand
// ---------------------------------------------------------------------------

export interface Placed { piece: Piece; x0: number; x1: number; t0: number; t1: number }

export class Tape {
  placed: Placed[] = [];
  private queue: Piece[] = [];
  private loop: Piece[] = [];
  private loopIndex = 0;

  constructor(program: Program, startX = 0, startT = 0) {
    this.endX = startX; this.endT = startT;
    this.setProgram(program);
  }
  endX: number; endT: number;

  private setProgram(p: Program) {
    const ok = (q: Piece) => q.dur > 0 && q.len > 0;
    this.queue = p.intro.filter(ok);
    const loop = p.loop.filter(ok);
    this.loop = loop.length ? loop : [flat({ spacing: 100 }, 1)];
    this.loopIndex = 0;
  }

  private next(): Piece {
    const q = this.queue.shift();
    if (q) return q;
    const piece = this.loop[this.loopIndex % this.loop.length]; this.loopIndex++;
    return piece;
  }
  private append(piece: Piece) {
    this.placed.push({ piece, x0: this.endX, x1: this.endX + piece.len, t0: this.endT, t1: this.endT + piece.dur });
    this.endX += piece.len; this.endT += piece.dur;
  }
  /** Writes tape until it reaches time t. */
  ensureT(t: number) { let guard = 0; while (this.endT < t + 1e-9 && guard++ < 100000) this.append(this.next()); }
  /** Writes tape until it reaches position x. */
  ensureX(x: number) { let guard = 0; while (this.endX < x + 1e-9 && guard++ < 100000) this.append(this.next()); }

  private find(pred: (p: Placed) => boolean): Placed | undefined {
    for (let i = this.placed.length - 1; i >= 0; i--) if (pred(this.placed[i])) return this.placed[i];
    return undefined;
  }
  /** Where the head is at time t, and the trace height there. */
  at(t: number): { x: number; y: number; p: Placed } {
    this.ensureT(t);
    const p = this.find((q) => q.t0 <= t) ?? this.placed[0];
    const u = p.piece.dur > 0 ? clamp((t - p.t0) / p.piece.dur, 0, 1) : 0;
    return { x: p.x0 + u * p.piece.len, y: yAt(p.piece, u), p };
  }
  /** The trace height at tape position x. */
  yAtX(x: number): number {
    this.ensureX(x);
    const p = this.find((q) => q.x0 <= x) ?? this.placed[0];
    return yAt(p.piece, p.piece.len > 0 ? clamp((x - p.x0) / p.piece.len, 0, 1) : 0);
  }
  /** The time at which the head reaches position x. */
  timeAtX(x: number): number {
    this.ensureX(x);
    const p = this.find((q) => q.x0 <= x) ?? this.placed[0];
    return p.t0 + (p.piece.len > 0 ? clamp((x - p.x0) / p.piece.len, 0, 1) : 0) * p.piece.dur;
  }
  /**
   * Replaces everything after the head at time t with a new program (and an optional one-off piece first).
   * The tape already drawn stays exactly as it was; a short bridge carries the head back to the baseline.
   */
  rewrite(t: number, program: Program, first: Piece[] = [], sc?: Scale) {
    const { x, y, p } = this.at(t);
    const idx = this.placed.indexOf(p);
    const u = p.piece.dur > 0 ? clamp((t - p.t0) / p.piece.dur, 0, 1) : 0;
    this.placed.length = idx; // drop the current piece and everything after it…
    if (u > 0) this.placed.push({ piece: cut(p.piece, u), x0: p.x0, x1: x, t0: p.t0, t1: t }); // …then keep its drawn part
    this.endX = x; this.endT = t;
    this.setProgram(program);
    const lead: Piece[] = [];
    if (Math.abs(y) > 0.015 && sc) lead.push(bridge(sc, y));
    this.queue.unshift(...lead, ...first);
  }
  /** Forgets tape that has scrolled out of view. */
  prune(beforeX: number) {
    let n = 0; while (n < this.placed.length - 1 && this.placed[n].x1 < beforeX) n++;
    if (n) this.placed.splice(0, n);
  }
}

/** Linear interpolation of a piece's knots at u. */
export function yAt(piece: Piece, u: number): number {
  const k = piece.knots;
  if (u <= k[0].u) return k[0].y;
  for (let i = 1; i < k.length; i++) {
    if (u <= k[i].u) {
      const a = k[i - 1]; const b = k[i]; const span = b.u - a.u;
      return span <= 0 ? b.y : a.y + ((u - a.u) / span) * (b.y - a.y);
    }
  }
  return k[k.length - 1].y;
}
function cut(piece: Piece, u: number): Piece {
  const knots = piece.knots.filter((k) => k.u < u).map((k) => ({ ...k, u: k.u / u }));
  knots.push({ u: 1, y: yAt(piece, u) });
  return { name: piece.name, len: piece.len * u, dur: piece.dur * u, knots, extras: piece.extras?.filter((e) => (e.kind === 'dot' ? e.u < u : e.u1 <= u)).map((e) => (e.kind === 'dot' ? { ...e, u: e.u / u } : { ...e, u0: e.u0 / u, u1: e.u1 / u })) };
}

// ---------------------------------------------------------------------------
// Strips: what the component draws and animates
// ---------------------------------------------------------------------------

export interface Geometry {
  width: number; height: number;
  /** Screen x of the beam head (the line ends there). */
  headX: number;
  /** Baseline and amplitude in px within the Pulse's own box. */
  baseline: number; amp: number;
  /** Extra room above and below the box inside the strip, so big spikes are never clipped. */
  pad: number;
}
/** The beam head: a dot of HEAD_R × stroke with a soft halo HALO times wider. */
export const HEAD_R = 1.55;
export const HALO = 2.3;
export function geometryFor(width: number, height: number, stroke: number): Geometry {
  // The head's halo stays inside the box, so a full-width Pulse never pokes past its container.
  const halo = stroke * HEAD_R * HALO;
  return { width, height, headX: Math.max(4, width - halo - 0.5), baseline: height * 0.64, amp: height * 0.5, pad: Math.ceil(height * 0.6) };
}
export const strokeFor = (height: number) => clamp(Math.round(height * 0.072 * 10) / 10, 1.5, 7);

export interface StripFrame { offset: number; x: number }
export interface HeadFrame { offset: number; y: number; s: number; o: number }
export interface Strip {
  /** Tape x at the strip's left edge. */
  originX: number;
  /** Strip size in px (height includes `pad` above and below). */
  width: number; height: number;
  /** The trace, in strip coordinates. */
  d: string;
  /** The crown's band and jewels, in strip coordinates. */
  lines: Array<{ x1: number; x2: number; y: number }>;
  dots: Array<{ cx: number; cy: number; r: number }>;
  /** translateX of the strip over the strip's duration. */
  scroll: StripFrame[];
  /** The head's translateY (within the Pulse box), scale and opacity. */
  head: HeadFrame[];
  /** Seconds after the strip starts at which the Pulse jolts or throws sparks. */
  shakes: number[]; sparks: number[];
  duration: number;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Cuts the strip that covers head times [t0, t1]. */
export function buildStrip(tape: Tape, t0: number, t1: number, g: Geometry, stroke: number): Strip {
  const h0 = tape.at(t0).x; const h1 = tape.at(t1).x;
  const originX = h0 - g.headX;
  const xEnd = h1 + (g.width - g.headX) + 2;
  tape.ensureX(xEnd);
  const yPx = (y: number) => r2(g.pad + g.baseline - y * g.amp);
  const X = (x: number) => r2(x - originX);

  let d = ''; const lines: Strip['lines'] = []; const dots: Strip['dots'] = [];
  const scroll: StripFrame[] = [{ offset: 0, x: 0 }]; const head: HeadFrame[] = []; const shakes: number[] = []; const sparks: number[] = [];
  const dur = Math.max(1e-6, t1 - t0);
  let lastX = -Infinity;
  const point = (x: number, y: number) => {
    const px = X(x);
    if (px < lastX) return; // never step backwards (a cut knot can repeat its edge)
    d += d ? `L${px} ${yPx(y)}` : `M${px} ${yPx(y)}`;
    lastX = px;
  };
  let started = false;
  for (const p of tape.placed) {
    if (p.x1 < originX - 1 || p.x0 > xEnd) continue;
    const { piece } = p;
    if (!started) { point(Math.max(originX, p.x0), yAt(piece, piece.len > 0 ? clamp((originX - p.x0) / piece.len, 0, 1) : 0)); started = true; }
    for (const k of piece.knots) {
      const x = p.x0 + k.u * piece.len;
      if (x < originX || x > xEnd) continue;
      point(x, k.y);
    }
    for (const e of piece.extras ?? []) {
      if (e.kind === 'line') lines.push({ x1: X(p.x0 + e.u0 * piece.len), x2: X(p.x0 + e.u1 * piece.len), y: yPx(e.y) });
      else dots.push({ cx: X(p.x0 + e.u * piece.len), cy: yPx(e.y), r: r2(e.r * stroke) });
    }
    // The strip scrolls at each piece's own speed: a keyframe at every boundary inside the window.
    if (p.t0 > t0 && p.t0 < t1) scroll.push({ offset: (p.t0 - t0) / dur, x: -r2(p.x0 - h0) });
    // The head rides every knot crossed during the window.
    for (const k of piece.knots) {
      const t = p.t0 + k.u * piece.dur;
      if (t < t0 || t > t1) continue;
      head.push({ offset: (t - t0) / dur, y: r2(g.baseline - k.y * g.amp), s: k.s ?? 1, o: k.o ?? 1 });
    }
    if (piece.shakeAt !== undefined) { const t = p.t0 + piece.shakeAt * piece.dur; if (t >= t0 && t <= t1) shakes.push(t - t0); }
    if (piece.sparksAt !== undefined) { const t = p.t0 + piece.sparksAt * piece.dur; if (t >= t0 && t <= t1) sparks.push(t - t0); }
  }
  point(xEnd, tape.yAtX(xEnd));
  scroll.push({ offset: 1, x: -r2(h1 - h0) });
  // Bracket the head with exact values at both ends of the window, then order it.
  const a = tape.at(t0); const b = tape.at(t1);
  head.push({ offset: 0, y: r2(g.baseline - a.y * g.amp), s: 1, o: headOpacity(a.p.piece) }, { offset: 1, y: r2(g.baseline - b.y * g.amp), s: 1, o: headOpacity(b.p.piece) });
  head.sort((m, n) => m.offset - n.offset);
  return { originX, width: Math.ceil(xEnd - originX) + 2, height: g.height + g.pad * 2, d, lines, dots, scroll: dedupe(scroll), head: dedupe(head), shakes, sparks, duration: dur };
}
const headOpacity = (piece: Piece) => piece.knots[0]?.o ?? 1;
function dedupe<T extends { offset: number }>(frames: T[]): T[] {
  const out: T[] = [];
  for (const f of frames) { const o = clamp(f.offset, 0, 1); if (out.length && Math.abs(out[out.length - 1].offset - o) < 1e-7) out[out.length - 1] = { ...f, offset: o }; else out.push({ ...f, offset: o }); }
  return out;
}

// ---------------------------------------------------------------------------
// Still frames (reduced motion, server rendering, browsers without the Web Animations API)
// ---------------------------------------------------------------------------

/** Lays down a little steady history, switches to the mood, and returns the head time that shows the mood best. */
export function poseTape(mood: PulseMood, sc: Scale, g: Geometry, seed = 1, bpm?: number): { tape: Tape; t: number } {
  const amp = g.amp;
  const v = speedFor(sc);
  const tape = new Tape(moodProgram('steady', sc, seed, bpm));
  const lead = (g.headX + v) / v; // enough history to fill the window
  tape.ensureT(lead);
  // Switch on a beat boundary so the still frame starts clean.
  const boundary = tape.placed.find((p) => p.t0 >= lead - 0.4)?.t0 ?? lead;
  tape.rewrite(boundary, moodProgram(mood, sc, seed, bpm, amp * 2), [], sc);
  const at = (name: string, u: number, screenFrac: number, nth = 0) => {
    tape.ensureT(boundary + 12);
    const p = tape.placed.filter((q) => q.t0 >= boundary - 1e-9 && q.piece.name === name)[nth];
    if (!p) return boundary + 1.2;
    const x = p.x0 + u * p.piece.len; // put this point at screenFrac of the width
    return tape.timeAtX(x + (g.headX - screenFrac * g.width));
  };
  switch (mood) {
    case 'steady': return { tape, t: at('sinus', 0.283, 0.56) };
    case 'racing': return { tape, t: at('sinus', 0.4, 0.84, 5) };
    case 'spike': return { tape, t: at('burst', 0.2, 0.52) };
    case 'jolt': return { tape, t: at('shock', 0.12, 0.6) };
    case 'flat': return { tape, t: at('flat', 0.1, 1) };
    case 'crown': return { tape, t: at('crown', 0.5, 0.5) };
  }
}

/** The still frame for a mood: the trace up to the head, and where the head sits. */
export function pose(mood: PulseMood, width: number, height: number, stroke: number, seed = 1, bpm?: number) {
  const sc = scaleFor(width, height); const g = geometryFor(width, height, stroke);
  const { tape, t } = poseTape(mood, sc, g, seed, bpm);
  const strip = buildStrip(tape, t, t + 1e-4, g, stroke);
  const headAt = tape.at(t);
  return { strip, g, headY: g.baseline - headAt.y * g.amp, headOpacity: headOpacity(headAt.p.piece) };
}
